import { forward, toPoint } from 'mgrs';

/** An easting and a northing of 2 to 5 digits each: a square from 1 km down to 1 m. */
export const MIN_MGRS_DIGITS = 4;
export const MAX_MGRS_DIGITS = 10;

const MGRS_PATTERN = /^(\d{1,2})([C-HJ-NP-X])([A-HJ-NP-Z]{2})(\d*)$/;

/** Trims the reference, removes any spaces in it and makes it upper case. */
export function normalizeMgrs(value: string | null | undefined): string {
  return (value ?? '').replace(/\s+/g, '').toUpperCase();
}

function withoutLeadingZeros(reference: string): string {
  return reference.replace(/^0+/, '');
}

/**
 * Whether a reference names a square that can be plotted:
 * - it is in a UTM zone (1 to 60) and has 4 to 10 digits, an equal number each for the easting and the
 *   northing, so it is a square from 1 km down to 1 m (a grid zone or 100 km square alone, or a 10 km square,
 *   is too coarse);
 * - the square exists. The package accepts any letters, so `18SAJ2308` converts to a point that is not in
 *   zone 18. To catch that, the 1 km square of the reference is converted to its center and back, and has to
 *   come back as the same reference. This is done on the 1 km square rather than on the full reference because
 *   at 1 m precision the package's rounding makes valid references come back one metre off.
 *
 * Spaces and lower case are accepted.
 */
export function isValidMgrs(value: string | null | undefined): boolean {
  const reference = normalizeMgrs(value);
  const match = MGRS_PATTERN.exec(reference);
  if (!match) {
    return false;
  }

  const [, zone, band, squareLetters, digits] = match;
  if (Number(zone) < 1 || Number(zone) > 60) {
    return false;
  }
  if (digits.length < MIN_MGRS_DIGITS || digits.length > MAX_MGRS_DIGITS || digits.length % 2 !== 0) {
    return false;
  }

  const half = digits.length / 2;
  const kilometerSquare = `${zone}${band}${squareLetters}${digits.slice(0, 2)}${digits.slice(half, half + 2)}`;

  try {
    const back = forward(toPoint(kilometerSquare), 2);
    return withoutLeadingZeros(back) === withoutLeadingZeros(kilometerSquare);
  } catch {
    return false;
  }
}

/**
 * The position to plot a reference at: the center of the grid square it names, or null if the reference is not
 * valid (see {@link isValidMgrs}).
 */
export function mgrsCenter(value: string | null | undefined): { latitude: number; longitude: number } | null {
  if (!isValidMgrs(value)) {
    return null;
  }

  try {
    const [longitude, latitude] = toPoint(normalizeMgrs(value));
    return Number.isFinite(latitude) && Number.isFinite(longitude) ? { latitude, longitude } : null;
  } catch {
    return null;
  }
}

/**
 * The reference, to the metre (10 digits), of the grid square a position is in, or null if the position has no
 * reference that can be plotted: it is not a position, or it is in a polar region, which MGRS covers with a
 * different grid (see {@link isValidMgrs}).
 */
export function mgrsFromLatLon(latitude: number, longitude: number): string | null {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
    return null;
  }

  try {
    const reference = forward([longitude, latitude], 5);
    return isValidMgrs(reference) ? reference : null;
  } catch {
    return null;
  }
}
